import cx from 'classnames';
import moment, {Moment} from 'moment';
import {useCallback, useEffect, useMemo, useState} from 'react';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import MomentLocaleUtils from 'react-day-picker/moment';

import {DateFormat, TimeFormat} from 'enums';
import {useLanguage} from 'hooks';

import './styles.scss';
import {FormControl} from '../FormControl';
import {TimePicker} from '../TimePicker';

type Props = {
    value: Moment;
    timeZone: string;
    dateFormat: DateFormat;
    timeFormat: TimeFormat;
    hasError?: boolean;
    errorMessage?: string;
    label?: string;
    className?: string;
    openDirection?: 'top' | 'bottom';
    now?: Moment;
    timeInputReadonly?: boolean;
    timeIncrement?: number;
    onChange: (value: Moment) => void;
}

export const DateTimePicker: React.FC<Props> = ({
    value, label, timeZone, dateFormat, timeFormat, hasError, errorMessage, className, openDirection = 'bottom', now: nowProp,
    timeInputReadonly = false, timeIncrement, onChange
}: Props) => {
    const [language] = useLanguage();
    const nowMoment = useMemo(() => nowProp || moment.tz(timeZone), [nowProp, timeZone]);
    const now = useMemo(() => nowMoment.toDate(), [nowMoment]);
    const valueDate = useMemo(() => value.format(dateFormat), [dateFormat, value]);

    const [timeValue, setTimeValue] = useState({hours: value.get('h'), minutes: value.get('m')});

    const handleChange = useCallback((changes: {date?: Date, time?: {hours: number, minutes: number}}) => {
        const currentValue = value.clone();
        const {date, time} = changes;
        if (changes.date) {
            const monthDay = date.getDate();
            const month = date.getMonth();
            const year = date.getFullYear();
            currentValue.set({date: monthDay, month, year});
        }
        if (changes.time) {
            currentValue.set(time);
        }
        onChange(currentValue);
    }, [value, onChange]);

    const handleTimeChange = useCallback((time: {hours: number, minutes: number}) => {
        handleChange({time});
    }, [handleChange]);

    const onDateChange = useCallback((_date: Date) => {
        handleChange({date: _date});
    }, [handleChange]);

    const formatDate = useCallback((_date: Date) => MomentLocaleUtils.formatDate(_date, dateFormat, timeZone), [dateFormat, timeZone]);

    useEffect(() => {
        setTimeValue({hours: value.get('h'), minutes: value.get('m')});
    }, [value]);

    return (
        <div>
            <div className={cx(className, 'app-date-time tw-flex')}>
                <FormControl {...{
                    value: valueDate, label, className: 'tw-flex-grow', hasError, errorMessage, skipErrorMessage: true
                }}
                >
                    {() => (
                        <DayPickerInput
                            value={valueDate}
                            classNames={{
                                container: 'DayPickerInput tw-flex-grow',
                                overlay: 'DayPickerInput-Overlay',
                                overlayWrapper: 'DayPickerInput-OverlayWrapper'
                            }}
                            inputProps={{
                                className: 'tw-bg-light tw-pl-3'
                            }}
                            overlayComponent={({children, onBlur, onFocus, classNames}) => (
                                <div
                                    className={cx(classNames,
                                        {
                                            'tw-bottom-0': openDirection === 'top',
                                            'tw-top-0': openDirection === 'bottom'
                                        },
                                        'tw-absolute tw-bg-light tw-z-3 tw-border tw-border-default'
                                    )}
                                    onBlur={onBlur}
                                    onFocus={onFocus}
                                >
                                    {children}
                                </div>
                            )}
                            formatDate={formatDate}
                            format={dateFormat}
                            dayPickerProps={{
                                localeUtils: MomentLocaleUtils,
                                locale: language,
                                showOutsideDays: true,
                                numberOfMonths: 1,
                                selectedDays: [value.toDate()],
                                disabledDays: {before: now},
                            }}
                            onDayChange={onDateChange}
                        />
                    )}
                </FormControl>
                <TimePicker
                    className='tw-w-36 tw-ml-4'
                    value={timeValue}
                    hasError={hasError}
                    timeIncrement={timeIncrement}
                    timeFormat={timeFormat}
                    timeZone={timeZone}
                    readonly={timeInputReadonly}
                    onChange={handleTimeChange}
                />
            </div>
            {hasError && <div className='tw-w-full tw-mt-2 tw-text-alert'>{errorMessage}</div>}
        </div>
    );
};